import type { Provider } from '@angular/core';
import { COUNTRY_CONFIG } from '../../shared/country/country.config';
import { AvailabilityRepository } from './availability/availability-repository';
import { SupabaseAvailabilityRepository } from './availability/availability-repository.supabase';
import { BrandRepository } from './brand/brand-repository';
import { SupabaseBrandRepository } from './brand/brand-repository.supabase';
import { CampaignLedgerService } from './campaign/ledger.service';
import { CatalogRepository } from './catalog/catalog-repository';
import { SupabaseCatalogRepository } from './catalog/catalog-repository.supabase';
import { CatalogStore } from './catalog/catalog-store';
import { CoverageCatalogRepository } from './coverage/coverage-catalog-repository';
import { SupabaseCoverageCatalogRepository } from './coverage/coverage-catalog-repository.supabase';
import { CoverageRequestRepository } from './coverage/coverage-request-repository';
import { SupabaseCoverageRequestRepository } from './coverage/coverage-request-repository.supabase';
import { EntitlementRepository } from './entitlement/entitlement-repository';
import { SupabaseEntitlementRepository } from './entitlement/entitlement-repository.supabase';
import { LeadCompanyRepository } from './lead/lead-company-repository';
import { SupabaseLeadCompanyRepository } from './lead/lead-company-repository.supabase';
import { MeasurementEvaluatorService } from './measurement/measurement-evaluator.service';
import { MemberPreferencesRepository } from './member-preferences/member-preferences-repository';
import { SupabaseMemberPreferencesRepository } from './member-preferences/member-preferences-repository.supabase';
import { ChannelDataStartRepository } from './observation/channel-data-start-repository';
import { SupabaseChannelDataStartRepository } from './observation/channel-data-start-repository.supabase';
import { ChannelDataStartStore } from './observation/channel-data-start-store';
import { ObservationAcquisitionService } from './observation/observation-acquisition.service';
import { ObservationRefreshService } from './observation/observation-refresh.service';
import { ObservationRepository } from './observation/observation-repository';
import {
  CachingObservationRepository,
  OBSERVATION_SOURCE,
} from './observation/observation-repository.cached';
import { SupabaseObservationRepository } from './observation/observation-repository.supabase';
import { OrganizationRepository } from './organization/organization-repository';
import { SupabaseOrganizationRepository } from './organization/organization-repository.supabase';
import { RegionalSalesRepository } from './regional-sales/regional-sales-repository';
import { SupabaseRegionalSalesRepository } from './regional-sales/regional-sales-repository.supabase';
import { ReportJobRepository } from './report-job/report-job-repository';
import { SupabaseReportJobRepository } from './report-job/report-job-repository.supabase';
import { ScorecardRepository } from './scorecard/scorecard-repository';
import { SupabaseScorecardRepository } from './scorecard/scorecard-repository.supabase';
import {
  SEGMENT_PRODUCT_TABLE,
  SegmentEvaluatorService,
} from './segment/segment-evaluator.service';
import {
  SEGMENT_MEMBERSHIP_CONFIG,
  type SegmentMembershipConfig,
  SegmentMembershipStore,
} from './segment/segment-membership-store';
import { SegmentRepository } from './segment/segment-repository';
import { SupabaseSegmentRepository } from './segment/segment-repository.supabase';
import { SessionRepository } from './session/session-repository';
import { SupabaseSessionRepository } from './session/session-repository.supabase';

export const portalDomainProviders: Provider[] = [
  { provide: SessionRepository, useClass: SupabaseSessionRepository },
  { provide: BrandRepository, useClass: SupabaseBrandRepository },
  { provide: OrganizationRepository, useClass: SupabaseOrganizationRepository },
  { provide: SegmentRepository, useClass: SupabaseSegmentRepository },
  { provide: CatalogRepository, useClass: SupabaseCatalogRepository },
  { provide: EntitlementRepository, useClass: SupabaseEntitlementRepository },
  { provide: MemberPreferencesRepository, useClass: SupabaseMemberPreferencesRepository },
  { provide: CoverageCatalogRepository, useClass: SupabaseCoverageCatalogRepository },
  { provide: CoverageRequestRepository, useClass: SupabaseCoverageRequestRepository },
  { provide: OBSERVATION_SOURCE, useClass: SupabaseObservationRepository },
  { provide: ObservationRepository, useClass: CachingObservationRepository },
  { provide: ChannelDataStartRepository, useClass: SupabaseChannelDataStartRepository },
  { provide: AvailabilityRepository, useClass: SupabaseAvailabilityRepository },
  { provide: RegionalSalesRepository, useClass: SupabaseRegionalSalesRepository },
  { provide: LeadCompanyRepository, useClass: SupabaseLeadCompanyRepository },
  { provide: ReportJobRepository, useClass: SupabaseReportJobRepository },
  { provide: ScorecardRepository, useClass: SupabaseScorecardRepository },
  CatalogStore,
  ChannelDataStartStore,
  ObservationAcquisitionService,
  ObservationRefreshService,
  CampaignLedgerService,
  MeasurementEvaluatorService,
  SegmentEvaluatorService,
  SegmentMembershipStore,
  {
    provide: SEGMENT_MEMBERSHIP_CONFIG,
    useFactory: (catalog: CatalogStore): SegmentMembershipConfig => ({
      categoryNodeMap: async () => {
        await catalog.ensureLoaded();
        return catalog.categoryNodeMap();
      },
    }),
    deps: [CatalogStore],
  },
  { provide: SEGMENT_PRODUCT_TABLE, useValue: COUNTRY_CONFIG['SE'].productTable },
];
